import GoogleMapReact, { type Maps } from 'google-map-react';
import { useMemo } from 'react';
import startcase from 'lodash.startcase';
import kebabCase from 'lodash.kebabcase';

import VictoriaGeoJsonLGA from './MapArea/VicLGAMap';
import VictoriaGeoJsonLPHU from './MapArea/VicLPHUMap';
import VictoriaGeoJsonPHN from './MapArea/VicPHNMap';

import type { GeoChartDataInfo, GeoChartDataProps } from './_chart';
import { IsRunningInFEDEnv } from '../ChartPanel/_ChartServices';

const activeMapStyle = {
    strokeWeight: 3,
    strokeColor: '#FFD457',
    zIndex: 10,
};

const noDataMapStyle = {
    fillOpacity: 1,
    strokeWeight: 0.9,
    fillColor: '#F9F9F9',
    strokeColor: '#393E46',
};

const noParticipationMapStyle = {
    fillOpacity: 1,
    strokeWeight: 0.9,
    fillColor: '#E3E6E8',
    strokeColor: '#FFFFFF',
};

const mapCenter = {
    lat: -36.779592,
    lng: 145.266724,
};

const mapZoom = 7;

const mapStyles = [
    {
        featureType: 'administrative',
        stylers: [{ visibility: 'off' }],
    },
    {
        featureType: 'poi',
        stylers: [{ visibility: 'off' }],
    },
    {
        featureType: 'road',
        stylers: [{ visibility: 'off' }],
    },
    {
        featureType: 'transit',
        stylers: [{ visibility: 'off' }],
    },
    {
        featureType: 'water',
        stylers: [
            { color: '#E0F7FF' },
            { visibility: 'simplified' }
        ],
    },
    {
        featureType: 'landscape',
        stylers: [
            { visibility: 'simplified' },
            { color: '#E0F7FF' }
        ],
    }
];

const areaColor = {
    0: {
        color: '#E3E6E8',
        title: 'No data',
    },
    1: {
        color: '#B2E5F6',
        title: '< 40%',
    },
    2: {
        color: '#76d1ef',
        title: '40% - 49%',
    },
    3: {
        color: '#1bb2e4',
        title: '50% - 59%',
    },
    4: {
        color: '#106b89',
        title: '60% - 69%',
    },
    5: {
        color: '#001116',
        title: '>= 70%',
    },
};

const areaColorAlt1 = {
    0: {
        color: '#E3E6E8',
        title: 'No data',
    },
    1: {
        color: '#B2E5F6',
        title: '< 40%',
    },
    2: {
        color: '#88D7F2',
        title: '40% - 49%',
    },
    3: {
        color: '#66C2EE',
        title: '50% - 59%',
    },
    4: {
        color: '#259ECB',
        title: '60% - 69%',
    },
    5: {
        color: '#00779F',
        title: '70% - 79%',
    },
    6: {
        color: '#00435A',
        title: '80% - 89%',
    },
    7: {
        color: '#001116',
        title: '>= 90%',
    },
};

const areaColorAlt2 = {
    0: {
        color: '#E3E6E8',
        title: 'No data',
    },
    1: {
        color: '#B2E5F6',
        title: '< 10%',
    },
    2: {
        color: '#88D7F2',
        title: '10% - 19%',
    },
    3: {
        color: '#66C2EE',
        title: '20% - 29%',
    },
    4: {
        color: '#259ECB',
        title: '30% - 39%',
    },
    5: {
        color: '#00779F',
        title: '40% - 49%',
    },
    6: {
        color: '#00435A',
        title: '50% - 59%',
    },
    7: {
        color: '#001116',
        title: '>= 60%',
    },
};

type MapLegendProps = {
    legendTitle?: string;
    useAlternativeAreaColor?: string;
}

const RenderMapLegend = (props: MapLegendProps): JSX.Element => {
    const { legendTitle, useAlternativeAreaColor } = props;

    let areaColorToUse = { ...areaColor };
    if (useAlternativeAreaColor === 'alt1') {
        areaColorToUse = { ...areaColorAlt1 };
    } else if (useAlternativeAreaColor === 'alt2') {
        areaColorToUse = { ...areaColorAlt2 };
    }

    return (
        <div className="map-legend-container">
            <div className="map-legend-group">
                {legendTitle && (
                    <div className="map-legend-group-title">
                        {legendTitle}
                    </div>
                )}
                <div className="map-legend-group-content">
                    {Object.keys(areaColorToUse).map((areaKey, index) => {
                        const legend = areaColorToUse[areaKey as unknown as keyof typeof areaColorToUse];

                        if (!legend || legend.title.toLowerCase() === 'no data') {
                            return null;
                        }
                        const key = `${index}-${kebabCase(legend.title)}`;

                        return (
                            <div key={key} className="map-legend">
                                <div className="map-legend-color" style={{ backgroundColor: `${legend.color}` }} />
                                <div className="map-legend-title">
                                    {legend.title}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const infoContentSortOrder = {
    'eligible population': 1,
    'participants screened': 2,
    'participation': 3, // eslint-disable-line quote-props
    '95% confidence interval': 4,
};

const GeoChart = (props: GeoChartDataProps): React.ReactElement => {
    const { chartData, splitter, filter, mapType, googleMapsKey, chartDataType, chartDataContent, legendTitle, showMapLegend } = props;
    const allInfoWindows = {};
    const areaCodesWithNoParticipationData: string[] = [];

    let useAlternativeMapColor = 'standard';
    if (chartDataType === 'colposcopy' || chartDataType === 'colonoscopy') {
        useAlternativeMapColor = 'alt1';
    } else if (chartDataType === 'selfCollection') {
        useAlternativeMapColor = 'alt2';
    }

    let areaColorToUse = { ...areaColor };
    if (useAlternativeMapColor === 'alt1') {
        areaColorToUse = { ...areaColorAlt1 };
    } else if (useAlternativeMapColor === 'alt2') {
        areaColorToUse = { ...areaColorAlt2 };
    }

    const filterInfoContent = useMemo(() => {
        const result: string[] = [];
        const combined = {
            ...splitter,
            ...filter,
        };

        const replaceValue = {
            lga: 'LGA',
            lphu: 'LPHU',
            phn: 'PHN',
        };

        const timePeriodValue = splitter?.timePeriod ?? '';

        for (const [infoKey, infoValue] of Object.entries(combined)) {
            const displayValue = replaceValue[infoValue.toLowerCase() as unknown as keyof typeof replaceValue] ? replaceValue[infoValue.toLowerCase() as unknown as keyof typeof replaceValue] : infoValue;

            let displayInfoKey = infoKey;
            let displayDisplayValue = displayValue;
            let skipThisPair = false;

            if (chartDataType === 'screening') {
                if (displayInfoKey.includes('TimePeriod')) {
                    displayInfoKey = 'Year';
                }
            } else if (chartDataType === 'colonoscopy' || chartDataType === 'colposcopy' || chartDataType === 'selfCollection') {
                // if (displayInfoKey.includes('TimePeriod')) {
                if (displayInfoKey.includes('timePeriod')) {
                    displayInfoKey = 'Year';
                // } else if (infoKey.includes('CollectionName') {
                } else if (infoKey.includes('dataType')) {
                    if (timePeriodValue.includes('2023') && !infoKey.includes('2023')) {
                        // skip
                        skipThisPair = true;
                    } else if (!timePeriodValue.includes('2023') && infoKey.includes('2023')) {
                        // skip
                        skipThisPair = true;
                    }
                    displayInfoKey = 'Time to Assessment';
                    displayDisplayValue = startcase(displayDisplayValue);
                }
            }

            if (!skipThisPair) {
                const infoLine = `<li class="info-content-item filter-content-info">
                    <span class="info-content-key">${startcase(displayInfoKey)}</span>
                    <span class="info-content-value">${displayDisplayValue}</span>
                </li>`;
                result.push(infoLine);
            }
        }

        return result.join('');
    }, [splitter, filter]);

    const buildInfoContent = (infoLines: GeoChartDataInfo[]): string => {
        const result: string[] = [];

        const filterInfo = filterInfoContent;
        const sortedInfoLines = infoLines.sort((a, b) => {
            const aVal = infoContentSortOrder[a.infoKey.toLowerCase() as unknown as keyof typeof infoContentSortOrder] || 999;
            const bVal = infoContentSortOrder[b.infoKey.toLowerCase() as unknown as keyof typeof infoContentSortOrder] || 999;

            if (aVal > bVal) {
                return 1;
            }
            if (aVal < bVal) {
                return -1;
            }

            return 0;
        });

        sortedInfoLines.forEach(line => {
            const { infoKey } = line;
            const { infoValue } = line;
            if (infoKey) {
                let infoTitle = infoKey;
                if (chartDataType === 'screening') {
                    if (chartDataContent === 'bowel' || chartDataContent === 'breast') {
                        infoTitle = 'Persons screened ';
                    } else if (chartDataContent === 'cervical') {
                        infoTitle = 'Screening tests';
                    }
                }
                // else if (chartDataType === 'colonoscopy') {
                //     // TO CONFIRM: What should the label say?
                //     infoTitle = 'Colonoscopies completed ';
                // } else if (chartDataType === 'colposcopy') {
                //     infoTitle = 'Colposcopies completed ';
                // }
                // else if (chartDataType === 'selfCollection') {
                //     infoTitle = 'Self-collected tests ';
                // }

                const infoLine = `<li class="info-content-item">
                    <span class="info-content-key">${infoTitle}</span>
                    <span class="info-content-value">${infoValue}</span>
                </li>`;

                result.push(infoLine);
            }
        });

        return filterInfo + result.join('');
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleApiLoaded = (map: google.maps.Map, maps: Maps): void => {
        const isFed = IsRunningInFEDEnv();

        if (mapType === 'lphu') {
            // Load LPHU Polygon
            map.data.addGeoJson(VictoriaGeoJsonLPHU);
        } else if (mapType === 'phn') {
            // Load PHN Polygon
            map.data.addGeoJson(VictoriaGeoJsonPHN);
        } else {
            // Load LGA Polygon
            map.data.addGeoJson(VictoriaGeoJsonLGA);
        }

        const getAreaStyle = (value: string | number): {
            fillColor: string;
            fillOpacity: number;
            strokeColor?: string;
            strokeWeight: number;
            zIndex?: number;
        } => {
            const colorToUse = areaColorToUse[`${value}` as unknown as keyof typeof areaColorToUse].color || '#FF0000';
            return {
                fillColor: colorToUse,
                fillOpacity: 0.9,
                strokeColor: '#fdfdfd',
                strokeWeight: 0.9,
                zIndex: 1,
            };
        };

        map.data.forEach((feature: google.maps.Data.Feature) => {
            const areaName = (feature.getProperty('areaName'));
            const areaCode = (feature.getProperty('areaCode'));

            if (chartData[areaCode] && chartData[areaCode].value === '*') {
                areaCodesWithNoParticipationData.push(areaCode);
            }

            // if (!!areaCode && !!areaName) {
            if (areaCode) {
                const colorGroup = chartData[`${areaCode}`]?.colorGroup || 0;
                const areaStyle = getAreaStyle(colorGroup);
                const center = feature.getProperty('center');
                const pin = new google.maps.Marker({
                    map,
                    position: center,
                    icon: `/${isFed ? '' : 'ar/'}assets/images/invisible-pin.png`,
                    optimized: false,
                    clickable: false,
                });
                const areaData = chartData[`${areaCode}`] || { infoContent: [] };
                const infoTitle = areaData.infoTitle || areaName || '';
                const infoSubtitle = areaData.infoSubtitle || '';
                const areaInfoContent = buildInfoContent(areaData.infoContent);
                const infoWindowContent = `
                <div class="info-window">
                    <div class="info-title">
                        <h3>${infoTitle}</h3>
                        <p>${infoSubtitle}</p>
                    </div>
                    <div class="info-content">
                        <ul>${areaInfoContent}</ul>
                    </div>
                </div>`;

                const infoWindow = new google.maps.InfoWindow({ content: infoWindowContent });
                allInfoWindows[`${areaCode}`] = {
                    infoWindow,
                    pin,
                };

                if (areaCodesWithNoParticipationData.includes(areaCode)) {
                    map.data.overrideStyle(feature, noParticipationMapStyle);
                } else {
                    map.data.overrideStyle(feature, areaStyle);
                }
            } else {
                const center = feature.getProperty('center');
                const objId = feature.getProperty('objId');
                // const areaName = feature.getProperty('areaName');
                const pin = new google.maps.Marker({
                    map,
                    position: center,
                    icon: `/${isFed ? '' : 'ar/'}assets/images/invisible-pin.png`,
                    optimized: false,
                    clickable: false,
                });

                const labelText = areaName || 'unknown area';
                const infoWindowContent = `
                <div class="info-window">
                    <div class="info-title">
                        <h3>${labelText}</h3>
                        <p>No information available</p>
                    </div>
                </div>`;

                const infoWindow = new google.maps.InfoWindow({ content: infoWindowContent });
                allInfoWindows[`${objId}`] = {
                    infoWindow,
                    pin,
                };

                map.data.overrideStyle(feature, noDataMapStyle);
            }
        });

        const CloseAllInfoWindow = (): void => {
            const allKeys = Object.keys(allInfoWindows);
            allKeys.forEach(key => {
                allInfoWindows[`${key}`]?.infoWindow.close();
            });
        };

        const SwitchToInfoWindow = (key: string): void => {
            CloseAllInfoWindow();
            const areaInfoWindow = allInfoWindows[`${key}`].infoWindow;
            const areaPin = allInfoWindows[`${key}`].pin;
            if (areaInfoWindow && areaPin) {
                areaInfoWindow.open(map, areaPin);
            }
        };

        const SetPinLabel = (key: string, label: string | null): void => {
            const areaPin = allInfoWindows[`${key}`].pin;
            if (areaPin) {
                // areaPin.setLabel(label);
                if (label) {
                    areaPin.setLabel({
                        text: label,
                        // color: '#5DBDE9',
                        className: 'map-area-active-label',
                    });
                } else {
                    areaPin.setLabel(null);
                }
            }
        };

        // Events
        map.data.addListener('click', (event: google.maps.Data.MouseEvent) => {
            const areaCode = event.feature.getProperty('areaCode');
            if (areaCode) {
                SwitchToInfoWindow(areaCode);
            } else {
                const objId = event.feature.getProperty('objId');
                if (objId) {
                    SwitchToInfoWindow(objId);
                }
            }
        });

        map.data.addListener('mouseover', (event: google.maps.Data.MouseEvent) => {
            const areaCode = event.feature.getProperty('areaCode');
            const areaName = event.feature.getProperty('areaName');
            if (areaCode) {
                SetPinLabel(areaCode, areaName);
                map.data.overrideStyle(event.feature, activeMapStyle);
            }
        });

        map.data.addListener('mouseout', (event: google.maps.Data.MouseEvent) => {
            const areaCode = event.feature.getProperty('areaCode');
            if (areaCode) {
                SetPinLabel(areaCode, null);
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const colorGroup = chartData[`${areaCode}`]?.colorGroup || 0;
                const areaStyle = getAreaStyle(colorGroup);
                map.data.overrideStyle(event.feature, areaStyle);
            }
        });
    };

    return (
        <div className="map-container">
            <div className="map-wrapper" style={{ height: '650px' }}>
                <GoogleMapReact
                    // ref={mapRef}
                    bootstrapURLKeys={googleMapsKey ? { key: googleMapsKey } : undefined}
                    defaultCenter={mapCenter}
                    defaultZoom={mapZoom}
                    options={{
                        minZoom: 4,
                        maxZoom: 14,
                        styles: mapStyles,
                    }}
                    yesIWantToUseGoogleMapApiInternals={true}
                    onGoogleApiLoaded={({ map, maps }): void => {
                        handleApiLoaded(map, maps);
                    }}
                />
            </div>
            {showMapLegend && (<RenderMapLegend legendTitle={legendTitle} useAlternativeAreaColor={useAlternativeMapColor} />)}
        </div>
    );
};

export default GeoChart;
